import { useRouter } from "next/router";
import type { PropsWithChildren } from "react";

import { LoadingState } from "@/components/shared/layout/LoadingState";
import { RouteConfig } from "@/config/route.config";

import { AuthContext } from "./auth.context";

interface IProps {
  isFeatureEnabled?: boolean;
  type: "public-only" | "private";
  redirectTo?: string;
}

export const AuthGuard = ({ type, redirectTo, children, isFeatureEnabled }: PropsWithChildren<IProps>) => {
  const { user, isInitializing, isLoggedIn } = AuthContext.useAuth();
  const router = useRouter();

  // Show loading state while auth is initializing
  if (isInitializing) {
    return <LoadingState />;
  }

  // LaunchDarkly feature flag check
  if (isFeatureEnabled !== undefined && !isFeatureEnabled) {
    router.replace(redirectTo || RouteConfig.home);
    return <LoadingState />;
  }

  // For private routes, redirect and show loading if not logged in
  if (type === "private" && !isLoggedIn) {
    router.replace(RouteConfig.signin);
    return <LoadingState />;
  }

  // Additional check for user object on private routes
  if (type === "private" && !user) {
    router.replace(redirectTo || RouteConfig.home);
    return <LoadingState />;
  }

  // For public-only routes, redirect logged-in users
  if (type === "public-only" && user) {
    router.replace(redirectTo || RouteConfig.home);
    return <LoadingState />;
  }

  return children;
};
